import { useState } from "react";
import { onlineManager } from "@tanstack/react-query";

import AlertIcon from "@assets/icons/alert-icon.svg";
import OpenBoxIcon from "@assets/icons/open-box-icon.svg";

import useSystemStore from "@store/useSystemStore";
import { useTimeoutRef } from "@hooks/useTimeoutRef";
import { queryClient } from "@config/queryClient";
import { ERROR_MESSAGES, CONTACT_SUPPORT } from "@config/messages";
import { ImageSection } from "@components/Sections";
import { Paragraph } from "@ui/Content";
import { Spinner } from "@ui/Loaders";
import { Button } from "@ui/Button";
import { H3 } from "@ui/Headings";

const RETRY_FEEDBACK_MS = 1000;

interface Props {
  isVisible: boolean;
}

export default function ConnectionIssueScreen({ isVisible }: Props) {
  const [isPending, setIspending] = useState(false);
  const { incrementConnectionAttempt } = useSystemStore();
  const { schedule } = useTimeoutRef();

  const handleTryAgain = () => {
    setIspending(true);

    onlineManager.setOnline(navigator.onLine);
    // navigator.onLine can lie, so retry the bootstrap and the failed queries regardless.
    incrementConnectionAttempt();
    void queryClient.invalidateQueries();

    schedule(() => setIspending(false), RETRY_FEEDBACK_MS);
  };

  const animationClass = isVisible ? "animate-fade-in" : "animate-fade-out";

  return (
    <div className={`flex flex-col ${animationClass}`}>
      <div className="flex flex-col justify-center items-center pt-[64px] pb-[30px] px-4">
        <ImageSection
          mainIcon={<OpenBoxIcon className="z-20" />}
          overlayIcon={<AlertIcon className="text-yellow-500" />}
          overlayIconBackground="bg-yellow-100"
        />

        <H3 className="text-yellow-600 dark:text-yellow-600 my-[19px]">Connection Issue</H3>

        <div className="flex flex-col gap-4 pt-[32px] pb-6 px-4 border-t dark:border-gray-700">
          <Paragraph>{ERROR_MESSAGES.CONNECTION_LOST}</Paragraph>
          <Paragraph>{CONTACT_SUPPORT}</Paragraph>
        </div>

        <Button className="min-w-[270px]" buttonType={"PRIMARY"} onClick={handleTryAgain}>
          {isPending ? <Spinner size={20} /> : "Try Again"}
        </Button>
      </div>
    </div>
  );
}
